import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { memberApi, WALLPAPER } from "../../lib/member";
import "../../styles/farmstand-themes.css";

const ShellCtx = createContext(null);
const ART = { sky: "/images/themes/painted-sky.jpg", ...WALLPAPER };
const SKY_MOBILE = "/images/themes/painted-sky-portrait.webp";
export const useFarmstand = () => useContext(ShellCtx);

// Themed wrapper for Your Farmstand pages; renders nothing themed until membership is known, so no wrong-theme flash.
// account: wraps signed-in customer account pages; themed only when an active member chose "Entire member account".
// neutral: friend-profile visits/previews never inherit the VIEWER's look (setNeutral lets a page switch it for a sub-view).
export function FarmstandShell({ children, testid = "farmstand-shell", legacy = false, account = false, neutral = false }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [previewTheme, setPreviewTheme] = useState(null);
  const [forced, setNeutral] = useState(false);
  const plain = neutral || forced;

  const refresh = useCallback(async () => {
    try {
      setStatus(await memberApi.get("/status"));
    } catch {
      setStatus({ is_member: false, paid_customer: false, theme: "classic", profile: null });
    }
  }, []);

  useEffect(() => { setStatus(null); setPreviewTheme(null); refresh(); }, [user?.id, user?.subscription_status, refresh]);

  // Re-check membership whenever the member returns to the tab, so a lapse resets the look promptly.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", onVisible); };
  }, [refresh]);

  const acctOn = account && user && user.role !== "farmer" && status?.is_member && status.main_theme && status.main_theme !== "classic";
  const shown = !status?.is_member || plain ? "classic" : account ? status.main_theme : (previewTheme || status.theme || "classic");
  const themedNow = account ? !!acctOn : !!status;
  // Navy is a full dark look: header/footer follow it while a navy area is mounted; removed on unmount.
  useEffect(() => {
    if (!themedNow) return undefined;
    const h = document.querySelector("header");
    if (account && h) document.documentElement.style.setProperty("--af-header-h", `${Math.round(h.getBoundingClientRect().bottom)}px`);
    if (shown !== "navy") return undefined;
    document.body.dataset.fsNavy = "1";
    return () => { delete document.body.dataset.fsNavy; };
  }, [themedNow, shown, account]);
  if (account) {
    if (!acctOn) return children;
    legacy = true;
  }
  if (!status) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="farmstand-shell-loading">
        <div className="animate-pulse h-8 w-32 rounded" style={{ background: "rgba(15,29,47,0.12)" }} />
      </div>
    );
  }
  const theme = !status.is_member || plain ? "classic" : account ? status.main_theme : (previewTheme || status.theme || "classic");
  const art = ART[theme] ? { "--fs-art": `url(${ART[theme]})`, ...(theme === "sky" ? { "--fs-art-mobile": `url(${SKY_MOBILE})` } : {}) } : undefined;
  return (
    <ShellCtx.Provider value={{ status, setStatus, refresh, previewTheme, setPreviewTheme, theme, setNeutral }}>
      <div className={legacy ? "fs-themed fs-legacy" : "fs-themed"} data-fs-theme={theme} data-fs-scope={account ? "account" : "farmstand"} data-fs-neutral={plain ? "" : undefined} data-fs-wallpaper={WALLPAPER[theme] ? "" : undefined} data-fs-pattern={ART[theme] ? "" : undefined}
        style={art} data-testid={account ? "account-theme-shell" : testid}>
        {legacy ? <div className="fs-legacy-sheet">{children}</div> : children}
      </div>
    </ShellCtx.Provider>
  );
}
